import React from 'react';
import EquipmentSelector from '../EquipmentSelector';
import { TabProps } from './types';
import { getPriorityClasses, getPriorityIndicator, isVisibleAtDepth } from './helpers';

const Tab4EquipmentHistory: React.FC<TabProps> = ({
  formData,
  updateNestedField,
  depth,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Section 6: Equipment History</h3>

      {/* Current Equipment Satisfaction */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Current Equipment Satisfaction (1-10 scale)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Driver Satisfaction{getPriorityIndicator('equipment_history.current_driver_satisfaction')}
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.equipment_history?.current_driver_satisfaction || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                current_driver_satisfaction: e.target.value ? parseInt(e.target.value) : undefined
              })}
              className={getPriorityClasses('equipment_history.current_driver_satisfaction', !formData.equipment_history?.current_driver_satisfaction)}
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Iron Satisfaction{getPriorityIndicator('equipment_history.current_iron_satisfaction')}
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.equipment_history?.current_iron_satisfaction || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                current_iron_satisfaction: e.target.value ? parseInt(e.target.value) : undefined
              })}
              className={getPriorityClasses('equipment_history.current_iron_satisfaction', !formData.equipment_history?.current_iron_satisfaction)}
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Wedge Satisfaction{getPriorityIndicator('equipment_history.current_wedge_satisfaction')}
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.equipment_history?.current_wedge_satisfaction || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                current_wedge_satisfaction: e.target.value ? parseInt(e.target.value) : undefined
              })}
              className={getPriorityClasses('equipment_history.current_wedge_satisfaction', !formData.equipment_history?.current_wedge_satisfaction)}
            />
          </div>
        </div>
      </div>

      {/* Current Driver Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Current Driver</h4>
        {/* B11: 79% of real profiles ship with no driver loft at all — it's the one field here
            that decides whether we can tell your own head apart from a re-lofted one. */}
        <p className="text-xs text-gray-500 mb-3">
          Loft is stamped on the sole (e.g. 9°, 10.5°, 12°) — even a rough guess sharpens your fitting.
        </p>
        <EquipmentSelector
          clubType="driver"
          value={formData.equipment_history?.current_clubs_driver || {}}
          onChange={(data) => updateNestedField('equipment_history', {
            current_clubs_driver: data
          })}
          getPriorityIndicator={getPriorityIndicator}
          getPriorityClasses={getPriorityClasses}
        />
      </div>

      {/* Current Irons Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Current Irons</h4>
        <EquipmentSelector
          clubType="iron"
          value={formData.equipment_history?.current_clubs_irons || {}}
          onChange={(data) => updateNestedField('equipment_history', {
            current_clubs_irons: data
          })}
          getPriorityIndicator={getPriorityIndicator}
          getPriorityClasses={getPriorityClasses}
        />
      </div>

      {/* Current Wedges Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Current Wedges</h4>
        <EquipmentSelector
          clubType="wedge"
          value={formData.equipment_history?.current_clubs_wedges || {}}
          onChange={(data) => updateNestedField('equipment_history', {
            current_clubs_wedges: data
          })}
          getPriorityIndicator={getPriorityIndicator}
          getPriorityClasses={getPriorityClasses}
        />
      </div>

      {/* Equipment Change & Budget */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Equipment Upgrade Patterns</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Equipment Change Frequency</label>
            <select
              value={formData.equipment_history?.equipment_change_frequency || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                equipment_change_frequency: e.target.value || undefined
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="0-1 year">0-1 year</option>
              <option value="1-3 years">1-3 years</option>
              <option value="3-5 years">3-5 years</option>
              <option value="5+ years">5+ years</option>
            </select>
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Budget Flexibility</label>
            <select
              value={formData.equipment_history?.budget_flexibility || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                budget_flexibility: e.target.value || undefined
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="strict">Strict</option>
              <option value="moderate">Moderate</option>
              <option value="flexible">Flexible</option>
            </select>
          </div>
        </div>
      </div>

      {/* Previous Brands */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Previously Used Brands (up to 5 each)</h4>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Previous Driver Brands</label>
            <input
              type="text"
              value={formData.equipment_history?.older_brands_used_driver?.join(', ') || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                older_brands_used_driver: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : undefined
              })}
              placeholder="e.g., Titleist, Callaway, TaylorMade, Ping, Cobra"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Previous Iron Brands</label>
            <input
              type="text"
              value={formData.equipment_history?.older_brands_used_irons?.join(', ') || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                older_brands_used_irons: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : undefined
              })}
              placeholder="e.g., Titleist, Callaway, TaylorMade, Ping, Cobra"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Previous Wedge Brands</label>
            <input
              type="text"
              value={formData.equipment_history?.older_brands_used_wedges?.join(', ') || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                older_brands_used_wedges: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : undefined
              })}
              placeholder="e.g., Titleist, Callaway, TaylorMade, Ping, Cobra"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
        </div>
      </div>

      {/* All Time Favorite Club */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">All Time Favorite Club</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Brand</label>
            <input
              type="text"
              value={formData.equipment_history?.all_time_favorite_club?.brand || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  brand: e.target.value || undefined
                }
              })}
              placeholder="e.g., Titleist, Callaway, TaylorMade"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Model</label>
            <input
              type="text"
              value={formData.equipment_history?.all_time_favorite_club?.model || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  model: e.target.value || undefined
                }
              })}
              placeholder="e.g., TSR3, SM9, AP2"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Club Type</label>
            <select
              value={formData.equipment_history?.all_time_favorite_club?.club_type || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  club_type: e.target.value || undefined
                }
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="Driver">Driver</option>
              <option value="3-Wood">3-Wood</option>
              <option value="5-Wood">5-Wood</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Iron">Iron</option>
              <option value="Wedge">Wedge</option>
              <option value="Putter">Putter</option>
            </select>
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Shaft Flex</label>
            <select
              value={formData.equipment_history?.all_time_favorite_club?.shaft_flex || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  shaft_flex: e.target.value || undefined
                }
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="Ladies">Ladies</option>
              <option value="Senior">Senior</option>
              <option value="Regular">Regular</option>
              <option value="Stiff">Stiff</option>
              <option value="Extra Stiff">Extra Stiff</option>
            </select>
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Shaft Model</label>
            <input
              type="text"
              value={formData.equipment_history?.all_time_favorite_club?.shaft_model || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  shaft_model: e.target.value || undefined
                }
              })}
              placeholder="Stock or specific model"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Shaft Weight (grams)</label>
            <input
              type="number"
              min="40"
              max="150"
              value={formData.equipment_history?.all_time_favorite_club?.shaft_weight || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                all_time_favorite_club: {
                  ...formData.equipment_history?.all_time_favorite_club,
                  shaft_weight: e.target.value ? parseInt(e.target.value) : undefined
                }
              })}
              placeholder="e.g., 65, 110"
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            />
          </div>
        </div>
      </div>

      {/* Custom Fitting Experience */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3">Professional Fitting Experience</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Custom Fitting History</label>
            <select
              value={formData.equipment_history?.custom_fitting_history?.toString() || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                custom_fitting_history: e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">Launch Monitor Experience</label>
            <select
              value={formData.equipment_history?.launch_monitor_experience?.toString() || ''}
              onChange={(e) => updateNestedField('equipment_history', {
                launch_monitor_experience: e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined
              })}
              className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
            >
              <option value="">Select</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* F3: folded in from the former Playing Profile tab. Rounds Per Year is collected once,
          in Basic Information (F5 — it was duplicated here before). */}
      {(depth === 'everything' || depth === 'plus') && (
        <>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Course Types & Conditions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">Typical Course Type</label>
                <select
                  value={formData.playing_profile?.typical_course_type || ''}
                  onChange={(e) => updateNestedField('playing_profile', {
                    typical_course_type: e.target.value || undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                >
                  <option value="">Select</option>
                  <option value="links">Links</option>
                  <option value="parkland">Parkland</option>
                  <option value="resort">Resort</option>
                  <option value="municipal">Municipal</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>
              {isVisibleAtDepth('playing_profile.course_difficulty', depth) && (
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Course Difficulty</label>
                  <select
                    value={formData.playing_profile?.course_difficulty || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      course_difficulty: e.target.value || undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  >
                    <option value="">Select</option>
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="challenging">Challenging</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              )}
              {isVisibleAtDepth('playing_profile.weather_conditions', depth) && (
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Weather Conditions</label>
                  <select
                    value={formData.playing_profile?.weather_conditions || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      weather_conditions: e.target.value || undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  >
                    <option value="">Select</option>
                    <option value="calm">Calm</option>
                    <option value="windy">Windy</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Performance Patterns</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">Scoring Consistency</label>
                <select
                  value={formData.playing_profile?.scoring_consistency || ''}
                  onChange={(e) => updateNestedField('playing_profile', {
                    scoring_consistency: e.target.value || undefined
                  })}
                  className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                >
                  <option value="">Select</option>
                  <option value="very_consistent">Very Consistent</option>
                  <option value="somewhat">Somewhat</option>
                  <option value="inconsistent">Inconsistent</option>
                </select>
              </div>
              {isVisibleAtDepth('playing_profile.performance_under_pressure', depth) && (
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Performance Under Pressure</label>
                  <select
                    value={formData.playing_profile?.performance_under_pressure || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      performance_under_pressure: e.target.value || undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  >
                    <option value="">Select</option>
                    <option value="improves">Improves</option>
                    <option value="same">Same</option>
                    <option value="degrades">Degrades</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {isVisibleAtDepth('playing_profile.driver_under_pressure', depth) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Club Performance Under Pressure (1-10 scale, 10 being great under pressure)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Driver Under Pressure</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.playing_profile?.driver_under_pressure || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      driver_under_pressure: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Irons Under Pressure</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.playing_profile?.irons_under_pressure || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      irons_under_pressure: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">Wedges Under Pressure</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.playing_profile?.wedges_under_pressure || ''}
                    onChange={(e) => updateNestedField('playing_profile', {
                      wedges_under_pressure: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Tab4EquipmentHistory;
